import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { api } from "../lib/api";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import PasswordStrengthMeter from "../components/PasswordStrengthMeter";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const t = useT();
  const [f, setF] = useState({ full_name: "", email: "", password: "" });
  const [consent, setConsent] = useState(false);
  const [pwValid, setPwValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [gate, setGate] = useState({ open: true, message: null, loaded: false });

  useEffect(() => {
    api.get("/public/signups-status")
      .then((r) => setGate({ ...r.data, loaded: true }))
      .catch(() => setGate({ open: true, message: null, loaded: true }));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error(t("register.consent_required"));
      return;
    }
    setLoading(true);
    try {
      await register(f);
      toast.success(t("register.toast_created"));
      const params = new URLSearchParams(window.location.search);
      const amt = params.get("amount");
      api.post("/public/funnel", { kind: "register", amount: amt ? Number(amt) : null }).catch(() => {});
      nav(amt ? `/dashboard?issue_amount=${encodeURIComponent(amt)}` : "/dashboard");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 503) {
        // Admin flipped gate off mid-session — re-fetch and show banner
        setGate({ open: false, message: detail, loaded: true });
      }
      toast.error(detail || t("register.toast_failed"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <Header />
      <div className="max-w-md mx-auto px-6 py-20">
        <p className="text-xs tracking-[0.3em] uppercase text-[#4A5C50] mb-3">{t("register.eyebrow")}</p>
        <h1 className="serif text-5xl">{t("register.title")}</h1>
        {gate.loaded && !gate.open && (
          <div
            className="mt-8 rounded-2xl border border-[#CBA35C]/40 bg-[#CBA35C]/10 p-5"
            data-testid="register-maintenance-banner"
          >
            <div className="flex items-start gap-3">
              <Warning size={20} weight="duotone" className="text-[#8C6B21] shrink-0 mt-0.5" />
              <div className="text-sm text-[#8C6B21]">
                <p className="font-semibold">{t("register.gate_title")}</p>
                <p className="mt-1 text-[#4A5C50]">
                  {gate.message || t("register.gate_default_message")}
                </p>
                <p className="mt-3 text-[12px]">
                  {t("register.gate_already_member")}{" "}
                  <Link to="/login" className="text-[#184A30] underline">{t("register.signin_link")}</Link>.
                </p>
              </div>
            </div>
          </div>
        )}
        {gate.loaded && gate.open && (
          <>
            <form onSubmit={submit} className="mt-10 space-y-6" data-testid="register-form">
              <div>
                <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">{t("register.full_name_label")}</Label>
                <input className="underline-input" value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} required data-testid="register-name" />
              </div>
              <div>
                <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">{t("register.email_label")}</Label>
                <input type="email" className="underline-input mono" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required data-testid="register-email" />
              </div>
              <div>
                <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">{t("register.password_label")}</Label>
                <input type="password" className="underline-input mono" minLength={6} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required data-testid="register-password" />
                <PasswordStrengthMeter password={f.password} onValidityChange={setPwValid} />
              </div>
              <label
                className="flex items-start gap-3 cursor-pointer p-4 rounded-xl border border-[#E5E0D8] bg-stone-50 hover:bg-stone-100 transition-colors"
                data-testid="register-consent-label"
              >
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 h-5 w-5 rounded border-[#E5E0D8] text-[#184A30] focus:ring-[#184A30] shrink-0"
                  data-testid="register-consent-checkbox"
                />
                <span className="text-xs text-[#112318] leading-relaxed">
                  {t("register.consent.label")} <Link to="/terms" target="_blank" rel="noopener" className="text-[#184A30] underline">{t("register.consent.terms_link")}</Link>, <Link to="/privacy" target="_blank" rel="noopener" className="text-[#184A30] underline">{t("register.consent.privacy_link")}</Link>, {t("register.consent.and")} <Link to="/sms-policy" target="_blank" rel="noopener" className="text-[#184A30] underline" data-testid="register-consent-sms-link">{t("register.consent.sms_link")}</Link>.
                  <span className="block mt-1 text-[10px] text-[#4A5C50]">{t("register.consent.fineprint")}</span>
                </span>
              </label>
              <Button type="submit" disabled={loading || !pwValid || !consent} className="w-full h-12 rounded-xl bg-[#184A30] hover:bg-[#0F3320] disabled:bg-[#184A30]/40 text-white" data-testid="register-submit">
                {loading ? t("register.submitting") : t("register.submit")}
              </Button>
            </form>
            <p className="text-sm text-[#4A5C50] mt-8">
              {t("register.already_member")} <Link to="/login" className="text-[#184A30] link-ul" data-testid="link-login">{t("register.signin_link")}</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
